import React, { FunctionComponent, SyntheticEvent } from "react";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import { FocusableHeader } from "../../../components";
import BackNextButtons from "./../BackNextButtons";
import { useTranslate } from "../../../helpers";
import PlanInfo from "./../PlanInfo";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import { ChoiceList } from "@cmsgov/ds-medicare-gov";
import { GetBenefitsFrom } from "../../../@types";

enum HowPay {
  DIRECT_PAY = "DirectPay",
  DEDUCT = "Deducted",
}

const Paying: FunctionComponent<EnrollmentFormProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();

  const handleHowPay = (e: SyntheticEvent<HTMLInputElement>): void => {
    const value = e.currentTarget.value;

    const premiumDirectPay = value === HowPay.DIRECT_PAY;
    const premiumDeducted = !premiumDirectPay;

    let newState = { ...enrollData, premiumDeducted, premiumDirectPay };
    if (premiumDirectPay) {
      newState = {
        ...newState,
        premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
      };
    }
    setEnrollData(newState);
  };

  const handleBenefitsFrom = (e: SyntheticEvent<HTMLInputElement>): void => {
    const premiumWithhold = e.currentTarget.value;
    setEnrollData({ ...enrollData, premiumWithhold });
  };

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.paying_for_your_plan")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />

        <span className="sr-only">
          {t("sr_only_additional_questions_may_appear")}
        </span>

        <section>
          <h3 className="ds-u-margin-top--4">{t("enroll_form.how_to_pay")}</h3>

          <p>{t("enroll_form.you_can_pay_two_ways")}</p>

          <ol>
            <li>{t("enroll_form.pay_option_1")}</li>
            <li>{t("enroll_form.pay_option_2")}</li>
          </ol>

          <p>{t("enroll_form.you_can_pick_how")}</p>

          <h3>{t("enroll_form.you_may_qualify")}</h3>

          <p>{t("enroll_form.if_you_have_limited_income")}</p>

          <ul>
            <li>{t("enroll_form.get_help_paying")}</li>
            <li>{t("enroll_form.have_no_coverage")}</li>
            <li>{t("enroll_form.have_no_late_enrollment_penalty")}</li>
          </ul>

          <p>
            {t("enroll_form.to_apply_for_extra_help")}
            <a href="https://www.socialsecurity.gov/prescriptionhelp">
              www.socialsecurity.gov/prescriptionhelp
            </a>
          </p>

          <h3>{t("enroll_form.what_to_do_if_pay_part_d")}</h3>

          <p>{t("enroll_form.if_you_have_to_pay_part_d")}</p>

          <p>{t("enroll_form.part_d_is_separate")}</p>

          <FormattedHTMLMessage
            id="enroll_form.do_not_pay"
            tagName="p"
            values={{ plan_name: plan.name }}
          />

          <h3>{t("enroll_form.how_to_pay_when_you_get_help")}</h3>

          <p>{t("enroll_form.when_another_organization_pays")}</p>

          <ul>
            <li>{t("enroll_form.if_your_entire_monthly_premium")}</li>
            <li>
              {t("enroll_form.if_you_dont_want_your_entire_premium_deducted")}
            </li>
          </ul>

          <ChoiceList
            type="radio"
            choices={[
              {
                label: t("enroll_form.have_the_plan_bill_me"),
                value: HowPay.DIRECT_PAY,
                checked: enrollData.premiumDirectPay,
              },
              {
                label: t("enroll_form.deduct_from_check"),
                value: HowPay.DEDUCT,
                checked: enrollData.premiumDeducted,
              },
            ]}
            label={
              <h3 className="ds-u-text-transform--none">
                {t("enroll_form.how_do_you_want_to_pay")}
              </h3>
            }
            aria-label={t("enroll_form.how_do_you_want_to_pay")}
            name="how_pay"
            onChange={handleHowPay}
          />

          {enrollData.premiumDeducted && (
            <>
              <ChoiceList
                type="radio"
                choices={[
                  {
                    label: t("social_security"),
                    value: GetBenefitsFrom.SSI,
                    checked: enrollData.premiumWithhold === GetBenefitsFrom.SSI,
                  },
                  {
                    label: t("rrb"),
                    value: GetBenefitsFrom.RRB,
                    checked: enrollData.premiumWithhold === GetBenefitsFrom.RRB,
                  },
                ]}
                label={
                  <h3 className="ds-u-text-transform--none">{`${t(
                    "enroll_form.i_get_benefits_from"
                  )} ${t("enroll_form.optional")}`}</h3>
                }
                aria-label={`${t("enroll_form.i_get_benefits_from")} ${t(
                  "enroll_form.optional"
                )}`}
                name="benefits_from"
                onChange={handleBenefitsFrom}
              />

              <h3>{t("enroll_form.paying_your_plan_premium")}</h3>

              <FormattedHTMLMessage id="enroll_form.long_payment_info" />
            </>
          )}
        </section>

        <BackNextButtons
          inReview={inReview}
          nextPage={nextPage}
          previousPage={previousPage}
        />
      </div>
    </div>
  );
};

export default Paying;
